import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { GetCompaniesParams } from '../interfaces/get-companies';
import { RequestError } from '../../../interfaces/error.interface';
import { GetCompanyResponse } from '../interfaces/get-company';
import { Company } from '../../../interfaces/company.interface';
import { GetUsersResponse } from '../interfaces/get-users';
import { CompanyUser } from '../../../interfaces/companyUser.interface';
import { CompanyImportation } from '../../../interfaces/companyImportation.interface';
import { GetImportationsResponse } from '../interfaces/get-importations';
import { Store } from '../../../interfaces/store.interface';
import { GetStoresResponse } from '../interfaces/get-stores';

export interface CompanyState {
  companies: Company[];
  options: GetCompaniesParams;
  company: Company | null;
  companyRole: string;
  users: CompanyUser[];
  stores: Store[];
  store: Store | null;
  importations: CompanyImportation[];
  selected: Company | null;
  errorCode: number | null;
}

const initialState: CompanyState = {
  companies: [],
  options: {},
  company: null,
  companyRole: '',
  selected: null,
  users: [],
  stores: [],
  store: null,
  importations: [],
  errorCode: null,
};

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    getCompaniesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getCompaniesSuccessApiAction: (state, action: PayloadAction<Company[]>) => {
      state.companies = action.payload;
    },
    getCompaniesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getCompanyInitApiAction: (state) => {
      state.errorCode = null;
    },
    getCompanySuccessApiAction: (state, action: PayloadAction<GetCompanyResponse>) => {
      state.company = {
        id: action.payload.id,
        name: action.payload.name,
        rut: action.payload.rut,
        apiKey: action.payload.api_key,
        selectedAvatar: action.payload.selectedAvatar,
        integration: action.payload.integration,
        ai_chat_enabled: action.payload.ai_chat_enabled,
        inventory_count_enabled: action.payload.inventory_count_enabled,
        stock_comparison_enabled: action.payload.stock_comparison_enabled
      };
      state.companyRole = 'admin';
      state.stores = action.payload.warehouses;
    },
    getCompanyFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getUsersInitApiAction: (state) => {
      state.errorCode = null;
    },
    getUsersSuccessApiAction: (state, action: PayloadAction<GetUsersResponse>) => {
      state.users = action.payload.users;
    },
    getUsersFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getImportationsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getImportationsSuccessApiAction: (state, action: PayloadAction<GetImportationsResponse>) => {
      state.importations = action.payload.importations;
    },
    getImportationsFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getStoresInitApiAction: (state) => {
      state.errorCode = null;
    },
    getStoresSuccessApiAction: (state, action: PayloadAction<Store[]>) => {
      state.stores = action.payload;
    },
    getStoresFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postCompanyInitApiAction: () => {},
    postCompanySuccessApiAction: () => {},
    postCompanyFailuireApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postVerifyDbInitApiAction: () => {},
    postVerifyDbSuccessApiAction: () => {},
    postVerifyDbFailuireApiAction: () => {},
    postVerifyS3InitApiAction: () => {},
    postVerifyS3SuccessApiAction: () => {},
    postVerifyS3FailuireApiAction: () => {},
    putCompanyInitApiAction: () => {},
    putCompanySuccessApiAction: () => {},
    putCompanyFailuireApiAction: () => {},
    deleteCompanyInitApiAction: () => {},
    deleteCompanySuccessApiAction: () => {},
    deleteCompanyFailuireApiAction: () => {},
    postUserInitApiAction: () => {},
    postUserSuccessApiAction: () => {},
    postUserFailuireApiAction: () => {},
    deleteUserInitApiAction: () => {},
    deleteUserSuccessApiAction: () => {},
    deleteUserFailuireApiAction: () => {},
    postStoreInitApiAction: () => {},
    postStoreSuccessApiAction: () => {},
    postStoreFailuireApiAction: () => {},
    putStoreInitApiAction: () => {},
    putStoreSuccessApiAction: () => {},
    putStoreFailuireApiAction: () => {},
    deleteStoreInitApiAction: () => {},
    deleteStoreSuccessApiAction: () => {},
    deleteStoreFailuireApiAction: () => {},
    postUserStoreInitApiAction: () => {},
    postUserStoreFailuireApiAction: () => {},
    deleteUserStoreInitApiAction: () => {},
    deleteUserStoreFailuireApiAction: () => {},
    setCompanyAction: (state, action: PayloadAction<Company>) => {
      state.selected = action.payload;
    },
    setStoreAction: (state, action: PayloadAction<Store | null>) => {
      state.store = action.payload;
    },
    setOptionsAction: (state, action: PayloadAction<GetCompaniesParams>) => {
      state.options = action.payload;
    },
    resetOptionsAction: (state) => {
      state.options = {};
    },
    companyClearAction: (state) => {
      state.companies = [];
      state.options = {};
      state.company = null;
      state.companyRole = '';
      state.selected = null;
      state.users = [];
      state.stores = [];
      state.store = null;
      state.importations = [];
      state.errorCode = null;
    },
  },
});

export const {
  getCompaniesInitApiAction,
  getCompaniesSuccessApiAction,
  getCompaniesFailureApiAction,
  getCompanyInitApiAction,
  getCompanySuccessApiAction,
  getCompanyFailureApiAction,
  getUsersInitApiAction,
  getUsersSuccessApiAction,
  getUsersFailureApiAction,
  postCompanyInitApiAction,
  postCompanySuccessApiAction,
  postCompanyFailuireApiAction,
  putCompanyInitApiAction,
  putCompanySuccessApiAction,
  putCompanyFailuireApiAction,
  deleteCompanyInitApiAction,
  deleteCompanySuccessApiAction,
  deleteCompanyFailuireApiAction,
  getStoresInitApiAction,
  getStoresSuccessApiAction,
  getStoresFailureApiAction,
  postStoreInitApiAction,
  postStoreSuccessApiAction,
  postStoreFailuireApiAction,
  putStoreInitApiAction,
  putStoreSuccessApiAction,
  putStoreFailuireApiAction,
  deleteStoreInitApiAction,
  deleteStoreSuccessApiAction,
  deleteStoreFailuireApiAction,
  postUserStoreInitApiAction,
  postUserStoreFailuireApiAction,
  deleteUserStoreInitApiAction,
  deleteUserStoreFailuireApiAction,
  postUserInitApiAction,
  postUserSuccessApiAction,
  postUserFailuireApiAction,
  deleteUserInitApiAction,
  deleteUserSuccessApiAction,
  deleteUserFailuireApiAction,
  postVerifyDbInitApiAction,
  postVerifyDbSuccessApiAction,
  postVerifyDbFailuireApiAction,
  postVerifyS3InitApiAction,
  postVerifyS3SuccessApiAction,
  postVerifyS3FailuireApiAction,
  getImportationsInitApiAction,
  getImportationsSuccessApiAction,
  getImportationsFailureApiAction,
  setCompanyAction,
  setStoreAction,
  setOptionsAction,
  resetOptionsAction,
  companyClearAction,
} = companySlice.actions;

export default companySlice.reducer;
